/**
 * Single-path ACL Save (S8 Task 8.11; D11 as amended by NOTES row A5). One request at a time (the pending latch of
 * `useSingleStorageRequest`; closing the editor cancels it). The answer is classified for the editor: `saved` (with
 * `verified` when main proved an ambiguous write landed by re-reading it), `path-changed` (412 — the path's identity
 * changed: reload the path), `acl-changed` (main's compare-then-write found another ACL: reload the ACL), `ambiguous`,
 * `refused` (authorization) or `error`.
 */
import type { StorageHnsAccessControl, StorageOperationError } from '../../../../../types/azureStorage';
import { toStorageOperationError } from '../../services/azureStorageApi';
import { useSingleStorageRequest } from '../../properties/hooks/useSingleStorageRequest';
import { aclSaveRequest, type AclEditorDraft } from '../aclDraft';
import { hnsSetAcl } from '../hnsApi';

export type AclSaveResult =
  | { kind: 'saved'; reread?: StorageHnsAccessControl; verified: boolean }
  | { kind: 'path-changed' | 'acl-changed' | 'ambiguous' | 'refused' | 'error'; error: StorageOperationError }
  | { kind: 'stale' };

export function classifyAclSaveError(error: StorageOperationError): Exclude<AclSaveResult, { kind: 'saved' | 'stale' }> {
  if (error.code === 'PathChanged') return { kind: 'path-changed', error };
  if (error.code === 'AclChanged') return { kind: 'acl-changed', error };
  if (error.category === 'ambiguous_outcome') return { kind: 'ambiguous', error };
  if (error.category === 'authorization') return { kind: 'refused', error };
  return { kind: 'error', error };
}

export function useHnsAclSave(connectionId: string) {
  const request = useSingleStorageRequest();
  const save = async (acl: StorageHnsAccessControl, draft: AclEditorDraft): Promise<AclSaveResult> => {
    const body = aclSaveRequest(acl, draft);
    const outcome = await request.run((requestId) => hnsSetAcl({ connectionId, ...body, requestId }));
    if (!outcome.current) return { kind: 'stale' };
    if (outcome.error !== undefined) return classifyAclSaveError(toStorageOperationError(outcome.error));
    const answer = outcome.value;
    if (!answer) return { kind: 'stale' };
    if (answer.ok === false) return classifyAclSaveError(answer.error);
    const { reread, verified } = answer.value;
    return { kind: 'saved', reread: reread.ok ? reread.value : undefined, verified: verified === true };
  };
  return { pending: request.pending, save, cancel: request.cancel };
}
